import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faBars,
  faChevronDown,
  faEye,
  faEdit,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import GondericiAliciFirmaModal from '../components/GondericiAliciFirmaModal';

function GondericiAliciFirmalar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    company_type: '',
    active: '',
  });
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage, setCompaniesPerPage] = useState(5);
  const [totalCompanies, setTotalCompanies] = useState(0);

  // Fetch companies from Supabase
  const fetchCompanies = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('sender_receiver_companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setCompanies(data || []);
      setTotalCompanies(data?.length || 0);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const openCompanyModal = async (companyId) => {
    if (companyId === 'add') {
      setSelectedCompany(null);
    } else {
      // Fetch company details from Supabase
      const { data, error } = await supabase
        .from('sender_receiver_companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) {
        console.error('Error fetching company:', error);
        return;
      }

      setSelectedCompany(data);
    }
    setIsModalOpen(true);
  };

  const handleSaveCompany = async () => {
    await fetchCompanies(); // Refresh the list after saving
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm('Bu firmayı silmek istediğinizden emin misiniz?')) {
      try {
        const { error } = await supabase
          .from('sender_receiver_companies')
          .delete()
          .eq('id', companyId);

        if (error) throw error;

        // Refresh the companies list
        await fetchCompanies();
      } catch (error) {
        console.error('Error deleting company:', error);
        setError('Failed to delete company');
      }
    }
  };

  // Define filterCompanies with useCallback
  const filterCompanies = useCallback(() => {
    let filtered = [...companies];

    // Search filter (name, contact person, or email)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (company) =>
          company.name?.toLowerCase().includes(searchLower) ||
          company.contact_person?.toLowerCase().includes(searchLower) ||
          company.email?.toLowerCase().includes(searchLower)
      );
    }

    // Company type filter
    if (filters.company_type) {
      filtered = filtered.filter(
        (company) => company.company_type === filters.company_type
      );
    }

    // Active filter
    if (filters.active !== '') {
      const isActive = filters.active === 'true';
      filtered = filtered.filter((company) => company.active === isActive);
    }

    setFilteredCompanies(filtered);
  }, [companies, filters]);

  // Update useEffect to handle filtering
  useEffect(() => {
    filterCompanies();
  }, [filterCompanies]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Get current companies
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(
    indexOfFirstCompany,
    indexOfLastCompany
  );
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Reset to first page when filters or companiesPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, companiesPerPage]);

  // Handle change in number of companies per page
  const handleCompaniesPerPageChange = (e) => {
    setCompaniesPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getCompanyTypeText = (type) => {
    switch (type) {
      case 'sender':
        return 'Gönderici';
      case 'receiver':
        return 'Alıcı';
      case 'both':
        return 'Her İkisi';
      default:
        return type;
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">
                Gönderici/Alıcı Firmalar
              </h1>
              <p className="text-sm text-gray-500">
                Toplam Firma: {totalCompanies}
              </p>
            </div>
            <button
              onClick={() => openCompanyModal('add')}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Firma Ekle
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firma Ara
                </label>
                <input
                  type="text"
                  name="search"
                  placeholder="Firma adı, kişi veya e-posta"
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firma Tipi
                </label>
                <select
                  name="company_type"
                  value={filters.company_type}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm Tipler</option>
                  <option value="sender">Gönderici</option>
                  <option value="receiver">Alıcı</option>
                  <option value="both">Her İkisi</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durum
                </label>
                <select
                  name="active"
                  value={filters.active}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm Durumlar</option>
                  <option value="true">Aktif</option>
                  <option value="false">Pasif</option>
                </select>
              </div>
            </div>
          </div>

          {/* Companies List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {error && (
              <div className="p-4 text-red-700 bg-red-100">{error}</div>
            )}

            {loading ? (
              <div className="p-4 text-center">Firmalar yükleniyor...</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Firma Adı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        İletişim
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Konum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tip
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Eylemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentCompanies.map((company) => (
                      <tr key={company.id}>
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">
                              {company.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {company.contact_person || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-400">
                              Kayıt Tarihi:{' '}
                              {new Date(company.created_at).toLocaleDateString(
                                'tr-TR',
                                {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                }
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {company.phone || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {company.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {company.city || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {company.country || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {getCompanyTypeText(company.company_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              company.active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {company.active ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <button
                            onClick={() => openCompanyModal(company.id)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                            Düzenle
                          </button>
                          <button
                            onClick={() => handleDeleteCompany(company.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Önceki
                      </button>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={companiesPerPage}
                          onChange={handleCompaniesPerPageChange}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                          currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Sonraki
                      </button>
                    </div>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700 mr-4">
                        Görüntülenen{' '}
                        <span className="font-medium">
                          {indexOfFirstCompany + 1}
                        </span>{' '}
                        -{' '}
                        <span className="font-medium">
                          {Math.min(
                            indexOfLastCompany,
                            filteredCompanies.length
                          )}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">
                          {filteredCompanies.length}
                        </span>{' '}
                        sonuç
                      </p>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={companiesPerPage}
                          onChange={handleCompaniesPerPageChange}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                            currentPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={faChevronLeft}
                            className="h-4 w-4"
                          />
                        </button>

                        {/* Page Numbers */}
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Show current page, first page, last page, and one page before and after current
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 &&
                              pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => paginate(pageNumber)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === pageNumber
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          }
                          // Show dots if there's a gap
                          if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return (
                              <span
                                key={pageNumber}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}

                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                            currentPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="h-4 w-4"
                          />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Add the modal */}
      <GondericiAliciFirmaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        company={selectedCompany}
        onSave={handleSaveCompany}
      />
    </div>
  );
}

export default GondericiAliciFirmalar;
